import { List } from "@refinedev/antd";
import { useCustom, useNavigation } from "@refinedev/core";
import { Alert, Button, Input, Select, Space, Table, Tag, Tooltip } from "antd";
import { useMemo, useState } from "react";
import { apiUrl } from "@/consts";
import type { LottoLicence, LottoLicenceStatus } from "./types";

const STATUS_LABEL: Record<LottoLicenceStatus, string> = {
  DRAFT: "Draft",
  SUBMITTED: "Awaiting review",
  APPROVED: "Approved",
  REJECTED: "Rejected",
};

const STATUS_COLOR: Record<LottoLicenceStatus, string> = {
  DRAFT: "default",
  SUBMITTED: "gold",
  APPROVED: "success",
  REJECTED: "red",
};

// Order shown in the status dropdown (review-first).
const STATUS_ORDER: LottoLicenceStatus[] = ["SUBMITTED", "APPROVED", "REJECTED", "DRAFT"];

type StatusFilter = LottoLicenceStatus | "ALL";
type ExpiryFilter = "ALL" | "EXPIRED" | "EXPIRING" | "VALID";

const EXPIRY_OPTIONS: Array<{ value: ExpiryFilter; label: string }> = [
  { value: "ALL", label: "Any expiry" },
  { value: "EXPIRED", label: "Expired" },
  { value: "EXPIRING", label: "Expiring ≤ 30 days" },
  { value: "VALID", label: "Valid" },
];

const EXPIRING_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

function formatDate(value: string | null) {
  return value ? new Date(value).toLocaleString() : "—";
}

function expiryState(value: string | null): ExpiryFilter | null {
  if (!value) return null;
  const expires = new Date(value).getTime();
  if (Number.isNaN(expires)) return null;
  const now = Date.now();
  if (expires < now) return "EXPIRED";
  if (expires - now <= EXPIRING_WINDOW_MS) return "EXPIRING";
  return "VALID";
}

function matchesSearch(licence: LottoLicence, term: string): boolean {
  const haystack = [
    licence.club?.name,
    licence.club?.slug,
    licence.licenseNumber,
    licence.issuingAuthority,
    licence.regulatorName,
    licence.jurisdiction,
    licence.licenceClass,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(term);
}

export function LottoLicenceList() {
  const { show } = useNavigation();
  const [status, setStatus] = useState<StatusFilter>("SUBMITTED");
  const [search, setSearch] = useState("");
  const [jurisdiction, setJurisdiction] = useState<string>("ALL");
  const [expiry, setExpiry] = useState<ExpiryFilter>("ALL");

  // Fetch the full set once and filter client-side: there is at most one licence
  // per club, so the dataset is small and instant cross-status filtering/search
  // beats round-tripping per filter change.
  const { data, isLoading, isFetching, isError, error, refetch } = useCustom<{
    list: LottoLicence[];
    total: number;
  }>({
    url: `${apiUrl}/lotto-license`,
    method: "get",
  });

  const allLicences = data?.data?.list ?? [];

  const statusCounts = useMemo(() => {
    const counts: Record<StatusFilter, number> = {
      ALL: allLicences.length,
      SUBMITTED: 0,
      APPROVED: 0,
      REJECTED: 0,
      DRAFT: 0,
    };
    for (const licence of allLicences) counts[licence.status] += 1;
    return counts;
  }, [allLicences]);

  const jurisdictionOptions = useMemo(() => {
    const set = new Set<string>();
    for (const licence of allLicences) {
      const value = licence.jurisdiction?.trim();
      if (value) set.add(value);
    }
    return [
      { value: "ALL", label: "All jurisdictions" },
      ...Array.from(set)
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({ value, label: value })),
    ];
  }, [allLicences]);

  const statusOptions = useMemo(
    () => [
      { value: "ALL" as StatusFilter, label: `All (${statusCounts.ALL})` },
      ...STATUS_ORDER.map((s) => ({
        value: s as StatusFilter,
        label: `${STATUS_LABEL[s]} (${statusCounts[s]})`,
      })),
    ],
    [statusCounts],
  );

  const term = search.trim().toLowerCase();
  const licences = useMemo(() => {
    return allLicences.filter((licence) => {
      if (status !== "ALL" && licence.status !== status) return false;
      if (jurisdiction !== "ALL" && licence.jurisdiction?.trim() !== jurisdiction) return false;
      if (expiry !== "ALL" && expiryState(licence.expiresAt) !== expiry) return false;
      if (term && !matchesSearch(licence, term)) return false;
      return true;
    });
  }, [allLicences, status, jurisdiction, expiry, term]);

  const hasFilters = status !== "SUBMITTED" || jurisdiction !== "ALL" || expiry !== "ALL" || term !== "";

  return (
    <List title="Lotto licences" resource="lotto-licences" canCreate={false}>
      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Could not load lotto licences"
          description={error?.message ?? "Please try again."}
          action={
            <Button size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      )}
      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder="Search club, licence no., authority…"
          style={{ minWidth: 280 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          style={{ minWidth: 190 }}
          value={status}
          options={statusOptions}
          onChange={setStatus}
        />
        <Select
          style={{ minWidth: 200 }}
          value={jurisdiction}
          options={jurisdictionOptions}
          onChange={setJurisdiction}
          showSearch
          optionFilterProp="label"
        />
        <Select
          style={{ minWidth: 180 }}
          value={expiry}
          options={EXPIRY_OPTIONS}
          onChange={setExpiry}
        />
        {hasFilters && (
          <Button
            onClick={() => {
              setStatus("SUBMITTED");
              setSearch("");
              setJurisdiction("ALL");
              setExpiry("ALL");
            }}
          >
            Reset filters
          </Button>
        )}
      </Space>
      <Table<LottoLicence>
        loading={isLoading || isFetching}
        dataSource={licences}
        rowKey="id"
        onRow={(record) => ({
          onClick: () => show("lotto-licences", record.id),
          style: { cursor: "pointer" },
        })}
        pagination={{
          showTotal: (count) => `${count} licence${count === 1 ? "" : "s"}`,
          showSizeChanger: true,
          defaultPageSize: 20,
          pageSizeOptions: [20, 50, 100],
        }}
        columns={[
          {
            title: "Club",
            key: "club",
            sorter: (a, b) =>
              (a.club?.name ?? "").localeCompare(b.club?.name ?? ""),
            render: (_, record) => record.club?.name ?? "Unknown club",
          },
          {
            title: "Licence no.",
            dataIndex: "licenseNumber",
            render: (value: string | null) => value || "—",
          },
          {
            title: "Jurisdiction",
            dataIndex: "jurisdiction",
            render: (value: string | null) => value || "—",
          },
          {
            title: "Status",
            dataIndex: "status",
            width: 150,
            render: (value: LottoLicenceStatus) => (
              <Tag color={STATUS_COLOR[value]}>{STATUS_LABEL[value]}</Tag>
            ),
          },
          {
            title: "Expires",
            dataIndex: "expiresAt",
            width: 150,
            sorter: (a, b) =>
              new Date(a.expiresAt ?? 0).getTime() - new Date(b.expiresAt ?? 0).getTime(),
            render: (value: string | null) => {
              if (!value) return "—";
              const state = expiryState(value);
              const label = new Date(value).toLocaleDateString();
              if (state === "EXPIRED") {
                return <Tag color="red">Expired · {label}</Tag>;
              }
              if (state === "EXPIRING") {
                return (
                  <Tooltip title="Expires within 30 days">
                    <Tag color="orange">{label}</Tag>
                  </Tooltip>
                );
              }
              return label;
            },
          },
          {
            title: "Submitted",
            dataIndex: "submittedAt",
            width: 200,
            defaultSortOrder: "descend",
            sorter: (a, b) =>
              new Date(a.submittedAt ?? 0).getTime() - new Date(b.submittedAt ?? 0).getTime(),
            render: formatDate,
          },
          {
            title: "Reviewed",
            dataIndex: "reviewedAt",
            width: 200,
            sorter: (a, b) =>
              new Date(a.reviewedAt ?? 0).getTime() - new Date(b.reviewedAt ?? 0).getTime(),
            render: formatDate,
          },
          {
            title: "Actions",
            key: "actions",
            width: 100,
            render: (_, record) => (
              <Button
                type="link"
                onClick={(e) => {
                  e.stopPropagation();
                  show("lotto-licences", record.id);
                }}
              >
                Open
              </Button>
            ),
          },
        ]}
      />
    </List>
  );
}
